import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const PrivacyPage = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <>
            <SEO 
                title="Privacy Policy | AircoreDynamics"
                description="Learn how AircoreDynamics protects your personal information and data privacy. Our commitment to user privacy and data security."
                keywords="privacy policy, data protection, user privacy, GDPR compliance"
                image="https://aircoredynamics.com/og-image.png"
                url="/privacy"
                type="website"
            />
            <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                transition={{ duration: 0.6 }}
                className="container mx-auto px-6 py-12 max-w-4xl"
            >
            {/* Header */}
            <div className="text-center mb-12">
                <ShieldCheckIcon className="h-16 w-16 mx-auto mb-4 text-light-primary dark:text-dark-primary" />
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                    Privacy <span className="gradient-text">Policy</span>
                </h1>
                <p className="text-light-text/70 dark:text-dark-text/70">
                    Last Updated: October 29, 2025
                </p>
            </div>

            {/* Content */}
            <div className="card p-8 space-y-8">
                
                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        1. Introduction
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        Welcome to AircoreDynamics. We respect your privacy and are committed to protecting your personal data. 
                        This privacy policy will inform you about how we look after your personal data when you visit our platform 
                        and tell you about your privacy rights and how the law protects you.
                    </p>
                </section>

                

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        2. Information We Collect
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed mb-4">
                        We collect and process the following types of information:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4">
                        <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
                        <li><strong>Profile Data:</strong> Profile picture, bio, educational background</li>
                        <li><strong>Learning Data:</strong> Course progress, quiz results, certificates earned</li>
                        <li><strong>Usage Data:</strong> Pages visited, time spent, interactions with content</li>
                        <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        3. How We Use Your Information
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed mb-4">
                        We use your personal data for the following purposes:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4">
                        <li>To provide and maintain our educational services</li>
                        <li>To personalize your learning experience</li>
                        <li>To track your course progress and issue certificates</li>
                        <li>To communicate with you about updates and new courses</li>
                        <li>To improve our platform and develop new features</li>
                        <li>To ensure security and prevent fraud</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        4. Data Security
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        We implement appropriate technical and organizational security measures to protect your personal data. 
                        All passwords are encrypted using industry-standard hashing algorithms. We use SSL/TLS encryption 
                        for data transmission and store data on secure servers with restricted access.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        5. Data Sharing
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        We do not sell your personal data to third parties. We may share your data only in the following circumstances:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4 mt-4">
                        <li>With your explicit consent</li>
                        <li>To comply with legal obligations</li>
                        <li>With service providers who assist in operating our platform (under strict confidentiality agreements)</li>
                        <li>In case of a business merger or acquisition (you will be notified)</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        6. AI Chat Service and Conversation Data
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed mb-4">
                        Our platform uses an AI chat feature that may process messages via third-party AI providers (for example, Google Generative AI - Gemini) to generate responses. We do not retain conversation transcripts long-term; messages are processed transiently to produce replies and are not stored as permanent records. Please avoid sharing sensitive personal information in chat messages.
                    </p>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        If you have questions about how chat data is processed or wish to request deletion of any data, please contact us via the <Link to="/contact">contact page</Link>.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        7. Your Rights
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed mb-4">
                        Under data protection laws, you have the following rights:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4">
                        <li><strong>Right to Access:</strong> Request copies of your personal data</li>
                        <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                        <li><strong>Right to Erasure:</strong> Request deletion of your data</li>
                        <li><strong>Right to Restrict Processing:</strong> Request limitation on how we use your data</li>
                        <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
                        <li><strong>Right to Object:</strong> Object to processing of your data</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        8. Cookies and Tracking Technologies
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed mb-3">
                        We use cookies, web beacons, and similar tracking technologies to improve your experience on our platform, analyze traffic, and deliver personalized educational content.
                    </p>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        Cookies help us remember your preferences, keep you logged in, analyze course interaction, and understand how users navigate through our website. You can choose to disable or selectively turn off cookies in your browser settings.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        9. Google AdSense & Third-Party Advertising Policy
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed mb-3">
                        We use third-party advertising companies, including <strong>Google AdSense</strong>, to serve advertisements when you visit our website.
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4 mb-4">
                        <li>
                            <strong>Third-Party Vendors:</strong> Third-party vendors, including Google, use cookies (including the DoubleClick cookie) to serve ads based on a user's prior visits to this website or other websites on the Internet.
                        </li>
                        <li>
                            <strong>Personalized Advertising:</strong> Google's use of advertising cookies enables it and its partners to serve ads to our users based on their visit to AircoreDynamics and/or other sites across the Internet.
                        </li>
                        <li>
                            <strong>Opting Out of Personalized Ads:</strong> Users may opt out of personalized advertising at any time by visiting <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-light-primary dark:text-dark-primary underline">Google Ads Settings</a>.
                        </li>
                        <li>
                            <strong>Industry Opt-Out Platforms:</strong> Alternatively, users can opt out of a third-party vendor's use of cookies for personalized advertising by visiting <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-light-primary dark:text-dark-primary underline">www.aboutads.info</a> or <a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-light-primary dark:text-dark-primary underline">Your Online Choices</a> (for European users).
                        </li>
                    </ul>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        Third-party ad servers or ad networks use technologies in their respective advertisements and links that appear on AircoreDynamics, which are sent directly to your browser. They automatically receive your IP address when this occurs. AircoreDynamics has no access to or control over these cookies that are used by third-party advertisers.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        10. Children's Privacy
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        Our platform is not intended for children under 13 years of age. We do not knowingly collect 
                        personal data from children under 13. If you are a parent or guardian and believe your child 
                        has provided us with personal data, please contact us.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        11. Changes to This Policy
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        We may update this privacy policy from time to time. We will notify you of any changes by 
                        posting the new policy on this page and updating the "Last Updated" date. We encourage you 
                        to review this policy periodically.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        12. Contact Us
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        If you have any questions about this privacy policy or our privacy practices, please contact us at:
                    </p>
                    <div className="mt-4 p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                        <p className="text-light-text dark:text-dark-text">
                            <strong>Email:</strong> privacy@aircoredynamics.com<br />
                            <strong>Address:</strong> Worldwide
                        </p>
                    </div>
                </section>

            </div>

            {/* Back Button */}
            <div className="text-center mt-8">
                <Link 
                    to="/" 
                    className="btn-secondary inline-block"
                >
                    Back to Home
                </Link>
            </div>
        </motion.div>
        </>
    );
};

export default PrivacyPage;
